/*
 * Pure helpers behind the navigation drawer: flattening the module tree,
 * resolving the current URL to a module (and its sub-page), searching modules
 * by name, alias or path, and deciding which entries a role may see.
 *
 * No React and no icon imports, so this file is unit-tested with plain jest
 * (navSearch.test.js) and can be reasoned about without a browser.
 */

export function normalize(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Modules and their children as one flat list.  A child inherits the parent's
 * permission module and adminOnly flag unless it declares its own, and carries
 * a `parent` reference so results can be labelled "Parent › Child".
 */
export function flattenNav(modules) {
  const out = [];
  for (const m of modules) {
    out.push({ ...m, kind: 'module', parent: null });
    for (const c of m.children || []) {
      out.push({
        ...c,
        kind: 'child',
        parent: m,
        module: c.module === undefined ? m.module : c.module,
        adminOnly: c.adminOnly === undefined ? m.adminOnly : c.adminOnly,
      });
    }
  }
  return out;
}

function pathMatches(prefix, pathname) {
  if (!prefix) return false;
  if (pathname === prefix) return true;
  return pathname.startsWith(prefix.endsWith('/') ? prefix : prefix + '/');
}

/**
 * Longest-prefix match of `pathname` against every module and child path
 * (plus their extra `match` prefixes).  Returns { entry, module, child } or
 * null when nothing in the drawer owns that URL.
 */
export function matchNavEntry(modules, pathname) {
  let best = null;
  let bestLen = -1;
  for (const e of flattenNav(modules)) {
    for (const p of [e.path, ...(e.match || [])]) {
      if (pathMatches(p, pathname) && p.length > bestLen) {
        best = e;
        bestLen = p.length;
      }
    }
  }
  if (!best) return null;
  return {
    entry: best,
    module: best.kind === 'module' ? best : best.parent,
    child: best.kind === 'child' ? best : null,
  };
}

function wordScore(text, t) {
  if (!text) return 0;
  if (text === t) return 120;
  if (text.startsWith(t)) return 100;
  const words = text.split(' ');
  if (words.some((w) => w.startsWith(t))) return 85;
  // "roles" should still find "Role & Permissions"
  if (words.some((w) => w.length >= 3 && t.startsWith(w))) return 70;
  if (text.includes(t)) return 60;
  return 0;
}

/**
 * Rank modules and sub-pages for a typed query.  Every token of the query has
 * to match somewhere (label, abbreviation, alias, parent label or path); an
 * exact label beats an exact abbreviation, which beats a label prefix, which
 * beats aliases; aliases outrank the parent's name, and modules edge out children on aliases outrank the parent's name, and modules edge out children on
 * a tie.  Callers that filter by permission afterwards should pass a large
 * `limit` and cut the list themselves, or restricted roles see a short list.  Returns [{ entry, score }] best first.
 */
export function searchNav(modules, query, { limit = 12 } = {}) {
  const q = normalize(query);
  if (!q) return [];
  const tokens = q.split(' ').filter(Boolean);
  const scored = [];
  flattenNav(modules).forEach((e, order) => {
    const label = normalize(e.label);
    const parentLabel = e.parent ? normalize(e.parent.label) : '';
    const keywords = (e.keywords || []).map(normalize);
    const abbrs = (e.abbr || []).map(normalize);
    const path = normalize(e.path);
    let score = 0;
    for (const t of tokens) {
      let best = wordScore(label, t);
      // `abbr` is the document code people type (GRN, PRC, CIOA, DC): an exact
      // hit outranks any label that merely starts with those letters (PRC 360)
      for (const a of abbrs) {
        if (a === t) best = Math.max(best, 115);
        else if (t.length >= 2 && a.startsWith(t)) best = Math.max(best, 90);
      }
      // an alias never outranks a label that starts with the query ('heat' -> Heat Traceability, not RM Inward)
      for (const k of keywords) best = Math.max(best, wordScore(k, t) - 25);
      if (parentLabel) best = Math.max(best, wordScore(parentLabel, t) - 50);
      if (best <= 0 && path.includes(t)) best = 20;
      if (best <= 0) {
        score = 0;
        break;
      }
      score += best;
    }
    if (score > 0) scored.push({ entry: e, score: score + (e.kind === 'module' ? 3 : 0), order });
  });
  scored.sort((a, b) => b.score - a.score || a.order - b.order);
  return scored.slice(0, limit).map(({ entry, score }) => ({ entry, score }));
}

/**
 * May this user see the entry?  Rules, in order:
 *   - adminOnly entries are for Super Admin only;
 *   - Super Admin sees everything;
 *   - an entry with no module is for everyone who is logged in;
 *   - when the session has no permission list at all (an older API that did
 *     not send one) nothing is hidden: the server still enforces every rule,
 *     and hiding the whole menu is worse than showing a screen that refuses;
 *   - otherwise the role needs any permission on any of the entry's modules.
 */
export function canSeeEntry(entry, { isSuperAdmin, permissionsLoaded, hasModuleAccess }) {
  if (entry.adminOnly) return !!isSuperAdmin;
  if (isSuperAdmin) return true;
  const mods = entry.module == null ? [] : Array.isArray(entry.module) ? entry.module : [entry.module];
  if (!mods.length) return true;
  if (!permissionsLoaded) return true;
  return !!hasModuleAccess(mods);
}

/** Modules grouped in list order: [{ group, label, items }]. */
export function groupModules(modules, labels) {
  const out = [];
  for (const m of modules) {
    const last = out[out.length - 1];
    if (last && last.group === m.group) last.items.push(m);
    else out.push({ group: m.group, label: labels[m.group] || null, items: [m] });
  }
  return out;
}
